import DrawerPortal from 'components/drawer/DrawerPortal';
import TimecardCenterContent from 'pages/timecards/timecard-center/TimecardCenterContent';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'store';
import { closeSubDrawer, toggleSubDrawer } from 'store/reducers/subdrawer';
import StartTrackingButton from './StartTrackingButton';
import TrackingWidget from './TrackingWidget';
import useTrackedTimecard from './useTrackedTimecard';

export default function SidebarTracker() {
    const dispatch = useDispatch();
    const isDrawerOpen = useSelector((state) => state.menu.drawerOpen);
    const isSubDrawerOpen = useSelector((state) => state.subDrawer.activeDrawerId === 'timecard-center');
    const currentTimecard = useTrackedTimecard();

    const toggleDrawer = useCallback(() => {
        dispatch(toggleSubDrawer('timecard-center'));
    }, [dispatch]);

    const closeDrawer = useCallback(() => {
        dispatch(closeSubDrawer());
    }, [dispatch]);

    // The start button is not rendered while its drawer is open, so the drawer has no opener to hand
    // focus back to: when it closes with focus left on the body, or in the panel on its way out, the button
    // rendered again takes it.
    const startButtonRef = useRef<HTMLButtonElement | null>(null);
    const wasSubDrawerOpenRef = useRef(isSubDrawerOpen);
    useEffect(() => {
        const focused = document.activeElement;
        const isFocusLeft =
            !focused || focused === document.body || !!focused.closest('#sub-drawer-portal-destination');
        if (wasSubDrawerOpenRef.current && !isSubDrawerOpen && isFocusLeft) {
            startButtonRef.current?.focus({ preventScroll: true });
        }
        wasSubDrawerOpenRef.current = isSubDrawerOpen;
    }, [isSubDrawerOpen]);

    const getTrackingComponent = (): JSX.Element | null => {
        if (currentTimecard) {
            return (
                <TrackingWidget
                    timecard={currentTimecard}
                    toggleTimecardModal={toggleDrawer}
                    closeTimecardModal={closeDrawer}
                />
            );
        }

        if (!isSubDrawerOpen) {
            return (
                <div className={isDrawerOpen ? 'p-4' : 'p-2'}>
                    <StartTrackingButton ref={startButtonRef} toggleDrawer={toggleDrawer} isDrawerOpen={isDrawerOpen} />
                </div>
            );
        }

        return null;
    };

    return (
        <>
            <DrawerPortal
                content={<TimecardCenterContent />}
                drawerId="timecard-center"
                titleId="timecards.start-tracking"
                width={744}
                fitsContent
                type="sub"
            />
            {getTrackingComponent()}
        </>
    );
}
